/**
 * ROUTE ELIGIBILITY AND THE ONLY PLACE A PATH IS EVER LOOKED AT.
 *
 * Two jobs, and they are separate on purpose.
 *
 *   1. EXCLUDE credential-bearing routes. `/a`, `/a/` and everything under
 *      `/a/**` carry an access token IN THE PATH SEGMENT — see
 *      router/routes.tsx for why the whole prefix is owned rather than just
 *      `/a/:token`. Query redaction is irrelevant to this: there is no query
 *      to redact. The exclusion is structural and is not conditional on
 *      consent, so a visitor who allowed analytics gets exactly the same
 *      nothing here as one who declined.
 *
 *   2. CLASSIFY an eligible route into one of a CLOSED SET of synthetic
 *      categories. The categories are constants in this file. Nothing derived
 *      from the URL is returned: not the slug, not a step name read from the
 *      path, not a search parameter, not a fragment, not the title. A category
 *      is chosen BY the path and never built FROM it, which is the difference
 *      between "booking_time" and a professional's handle ending up in a
 *      report.
 *
 * The return type says the same thing the code does: an ineligible route
 * returns `null`, and there is no category value that means "unknown route".
 * A path this file does not recognise is not measured — silence is the
 * fail-closed answer for a classifier, because the alternative is a bucket
 * that fills with whatever we did not think of.
 */

import { hasOwn } from "../../../../shared/lib/hasOwn";

/** Every category this surface may ever report. Closed set, no escape hatch. */
export const CATEGORIES = [
  "landing",
  "professional_profile",
  "booking_service",
  "booking_time",
  "booking_details",
  "booking_review",
  "booking_outcome",
] as const;

export type Category = (typeof CATEGORIES)[number];

/**
 * The step segment as the router spells it, mapped to its category.
 *
 * A table rather than `("booking_" + step)`. Concatenation would put a path
 * segment into an analytics value the moment the router gained a step this
 * file had never heard of — which is precisely the shape of leak this module
 * exists to make impossible. An unknown step falls off the end and the route
 * is not measured.
 *
 * NULL-PROTOTYPE, AND THIS IS NOT DEFENSIVE STYLE. The first version was an
 * object literal, so `/anna/book/constructor` looked up `Object.prototype`
 * and returned a FUNCTION. `?? null` catches `undefined` and not that, so the
 * route classified as non-null, satisfied the eligibility gate, and — with the
 * flag on — would have loaded the library on a 404 page with
 * `page_title: <Function>`. Found in review. A visitor types the URL; nothing
 * else is needed.
 *
 * The keys are the four CANONICAL booking steps. Account entry (`signin`, `create`)
 * and the `/book/date` compatibility redirect are
 * intentionally unmeasured; identity screens are not booking funnel steps.
 * `outcome` used to be here
 * and is gone: `/x/book/outcome` renders NotFound, because the outcome screen
 * lives inside the review step. `routes.test.ts` pins these keys equal to
 * `views/Book`'s canonical booking `STEPS` (excluding the date alias), so the
 * lists cannot drift again.
 */
const STEP_CATEGORY: Readonly<Record<string, Category>> = Object.assign(Object.create(null), {
  service: "booking_service",
  time: "booking_time",
  details: "booking_details",
  review: "booking_review",
});

/** The router's step segments, exported so a test can pin them to the table. */
export const STEP_SEGMENTS = Object.keys(STEP_CATEGORY);

/**
 * Route prefixes that carry a credential in the path.
 *
 * A LIST OF PREFIXES, matched on whole segments, rather than a list of exact
 * paths. `/a/{token}` is one shape today; the rule has to survive the next
 * one without an amendment, and a segment-wise prefix match covers `/a`,
 * `/a/`, `/a/anything` and `/a/anything/deeper` identically.
 */
// `checkout`: Stripe's return carries the checkout reference in its query
// until the page scrubs it (payments prep, design H2); the whole prefix
// latches analytics off, like the management link.
export const CREDENTIAL_PREFIXES = ["a", "checkout"] as const;

/**
 * PATH SEGMENTS AS THE ROUTER WOULD SEE THEM, NOT AS THE STRING SPLITS.
 *
 * The first version was `pathname.split("/").filter(Boolean)` compared
 * case-sensitively, and QE proved that disagrees with React Router in three
 * ways a visitor can actually reach:
 *
 *   /A                  the router matches `path: "/a"` CASE-INSENSITIVELY and
 *                       renders the appointment screen. The prefix test did
 *                       not, so the route classified as a professional profile
 *                       and — on an armed build — loaded the library on an
 *                       appointment-namespace document.
 *   /A/<token>          the real appointment view, refused only as
 *                       "ineligible-route", so the shutdown latch never fired.
 *   /a%2F<token>        an encoded slash, so the whole thing is ONE segment.
 *                       `routes.tsx` names exactly this — "the ones an email
 *                       client mangles" — as the reason the prefix is owned.
 *
 * So: percent-decode and re-split on any slash that decoding revealed. A
 * segment that cannot be decoded, OR that still carries a `%` when the decode
 * budget runs out, returns `null` — and both callers treat that as the unsafe
 * answer rather than the convenient one.
 *
 * CASE IS NOT FOLDED HERE, AND THAT IS THE SECOND CORRECTION.
 *
 * The first version lower-cased every segment. That closed the `/A/<token>`
 * credential gap and opened its mirror: React Router matches the LITERAL
 * segments of a path case-insensitively but hands `:step` to `BookStep`
 * verbatim, and `BookStep` compares case-sensitively — so `/x/book/Time`
 * renders NotFound while a folding classifier called it `booking_time`. On an
 * armed build that loaded the library on a 404 and credited a funnel step to a
 * page nobody saw. Reviewer and QE both reproduced it in both browsers.
 *
 * So each comparison now folds case exactly where the router folds it: the
 * credential prefix and the `book` literal are matched case-insensitively, the
 * step segment case-sensitively. Fold nowhere else.
 */
function segmentsOf(pathname: string): string[] | null {
  const out: string[] = [];
  for (const raw of pathname.split("/")) {
    if (raw.length === 0) continue;
    /*
     * DECODE UNTIL IT STOPS CHANGING, BOUNDED — AND REFUSE WHAT IS LEFT.
     *
     * `%252F` decodes to `%2F` and only then to `/`, so one pass leaves
     * `a%2f<token>` looking like an ordinary slug. No legitimate slug can
     * contain a percent — the grammar is lowercase, digits and hyphens — so
     * repeating costs nothing.
     *
     * THE BOUND USED TO FAIL OPEN. Four passes exhaust on
     * `/a%252525252F<token>` (five levels), and the old code simply used the
     * still-encoded result: the segment stayed `a%2f<token>`, which is not
     * `"a"`, so it classified as a professional profile and — on an armed
     * build — loaded the library on a document whose URL still carried the
     * token. The Director asked for exactly this case; review and QE both
     * reproduced it in both browsers.
     *
     * A residual `%` now returns null, which every caller treats as unsafe.
     * That reuses the fail-closed path the malformed case already took, and it
     * removes the latent coupling to React Router decoding exactly once.
     */
    let decoded = raw;
    try {
      for (let pass = 0; pass < 4; pass += 1) {
        const next = decodeURIComponent(decoded);
        if (next === decoded) break;
        decoded = next;
      }
    } catch {
      // A malformed escape is a URL we cannot reason about. Refusing to
      // classify it is the only answer that cannot be wrong.
      return null;
    }
    if (decoded.includes("%")) return null;
    for (const part of decoded.split("/")) {
      if (part.length > 0) out.push(part);
    }
  }
  return out;
}

/**
 * Is this a route that may never be measured, at any consent state?
 *
 * Exported separately from `categoryFor` because callers need to be able to
 * ask this question on its own — a route change into `/a/**` has to be able
 * to shut the adapter down, not merely fail to produce a category.
 */
export function isCredentialRoute(pathname: string): boolean {
  const segments = segmentsOf(pathname);
  // Undecodable. Treat it as credential-bearing: that latches the adapter off,
  // which is the safe direction for a path nobody can read.
  if (segments === null) return true;
  if (segments.length === 0) return false;
  // Case-insensitive, because React Router matches the literal `/a` that way.
  const first = (segments[0] as string).toLowerCase();
  return CREDENTIAL_PREFIXES.includes(first as (typeof CREDENTIAL_PREFIXES)[number]);
}

/**
 * The synthetic category for a path, or null if it may not be measured.
 *
 * `pathname` only. Not `location.href`, not `search`, not `hash` — this
 * signature is the guard, because a function that cannot see a query string
 * cannot leak one.
 */
export function categoryFor(pathname: string): Category | null {
  if (isCredentialRoute(pathname)) return null;

  const segments = segmentsOf(pathname);
  if (segments === null) return null;
  if (segments.length === 0) return "landing";

  // `/<slug>` — the professional's public page. The slug is READ to decide the
  // shape and is never carried out of this function.
  if (segments.length === 1) return "professional_profile";

  // `/<slug>/book/<step>`
  //
  // The `book` literal folds — the router matches literals case-insensitively.
  // The step does NOT: `BookStep` compares it against `STEPS` case-sensitively,
  // so `/x/book/Time` is a 404 and must not be measured as one of the five.
  if (segments.length === 3 && (segments[1] as string).toLowerCase() === "book") {
    const step = segments[2] as string;
    const category = hasOwn(STEP_CATEGORY, step) ? STEP_CATEGORY[step] : undefined;
    // BOTH CHECKS. `hasOwn` is what stops a prototype member being returned;
    // the membership test is what makes the return type true by construction
    // rather than by the table being right. A classifier whose containment
    // argument is "the table only contains categories" is one edit away from
    // not being.
    return category !== undefined && CATEGORIES.includes(category) ? category : null;
  }

  return null;
}
